import { Verified } from "@mui/icons-material"
import { Paper, Stack, Tooltip, Typography } from "@mui/material"
import Grid2 from "@mui/material/Unstable_Grid2/Grid2"
import { useStore } from "@nanostores/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { Link, useParams } from "react-router-dom"

import { getOverview } from "@/api/monitor-api"
import type { Pool } from "@/api/pools-api"
import { IdBlock } from "@/components/IdBlock"
import { InMemoryTable } from "@/components/InMemoryTable"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { Subheading } from "@/components/Subheading"
import TokenAudits from "@/components/TokenAudits"
import TokenCard from "@/components/TokenCard"
import BoostTokenCTA from "@/components/TokenPage/BoostTokenCTA"
import PoolRow from "@/components/TokenPage/PoolRow"
import RenounceOwnershipCTA from "@/components/TokenPage/RenounceOwnershipCTA"
import TokenStats from "@/components/TokenPage/TokenStats"
import useHopperPrice from "@/hooks/useHopper"
import useTokenDetails from "@/hooks/useTokenDetails"
import { $allPools, $poolsOverviewMap } from "@/stores/pools-store"
import { PoolOverview } from "@/types"
import { truncateId } from "@/utils/data-utils"

import aolinkLogo from "/aolink.svg"

export default function TokenPage() {
  const { tokenId } = useParams()
  const { data: token, isLoading } = useTokenDetails(tokenId ?? "")

  const pools = useStore($allPools)
  const overviewMap = useStore($poolsOverviewMap)

  const { data: tokenPrice, isLoading: isTokenPriceLoading } = useHopperPrice(tokenId ?? "", "usd")

  const tokenPools = useMemo(
    () => pools.filter((x) => x.baseToken === tokenId || x.quoteToken === tokenId),
    [pools, tokenId],
  )

  const missingPoolIds = useMemo(
    () => (!overviewMap ? [] : tokenPools.filter((x) => !overviewMap[x.id]).map((x) => x.id)),
    [tokenPools, overviewMap],
  )

  const { data: fullOverview } = useQuery({
    queryKey: ["full-overview", missingPoolIds],
    queryFn: async () => {
      if (!missingPoolIds) throw new Error("Invalid params")

      return getOverview(missingPoolIds).then((overview) => {
        const map = overview.reduce(
          (acc, overview) => ({
            ...acc,
            [overview.poolId]: overview,
          }),
          {} as Record<string, PoolOverview>,
        )
        console.log("onFullOverview", map)

        return map
      })
    },
    enabled: missingPoolIds.length > 0,
  })

  useEffect(() => {}, [])

  const richTokenPools = useMemo(
    () =>
      tokenPools.map((x) => ({
        ...x,
        volume_usd: overviewMap?.[x.id]?.volumeUsd || 0,
        liquidity_usd: overviewMap?.[x.id]?.liquidityUsd || 0,
      })),
    [tokenPools, overviewMap, tokenId],
  )

  if (isLoading || !overviewMap) {
    return <LoadingSpinner />
  }

  if (!token || !tokenId) {
    return <div>Token not found</div>
  }

  return (
    <Grid2 container spacing={4}>
      <Grid2 xs={12}>
        <Subheading>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={2} alignItems="center">
              {token.RenounceOwnership && (
                <Tooltip
                  placement="left"
                  title="Tokens is using the Ownership Renouncement pattern."
                >
                  <Verified color="success" />
                </Tooltip>
              )}
              <span>{token.Ticker}</span>
              &middot;
              {tokenPrice && (
                <span>{isTokenPriceLoading ? "..." : `$${tokenPrice.toFixed(6)}`}</span>
              )}
              &middot;
              <IdBlock
                label={truncateId(tokenId)}
                value={tokenId}
                href={`https://ao.link/#/entity/${tokenId}`}
                hideCopyToClipboard
              />
              &middot;
              <Link to={`https://ao.link/#/entity/${tokenId}`}>
                <img src={aolinkLogo} alt="ao.link" />
              </Link>
            </Stack>
          </Stack>
        </Subheading>
      </Grid2>
      <Grid2 xs={12} lg={3}>
        <Stack gap={4}>
          <TokenCard poolId={"test"} token={tokenId} hideCTA />
          <RenounceOwnershipCTA token={token} />
          <TokenAudits tokenId={token.TokenProcess} />
          <TokenStats />
        </Stack>
      </Grid2>
      <Grid2 xs={12} lg={9}>
        <Stack gap={4}>
          <BoostTokenCTA />
          <Stack gap={2}>
            <Typography variant="h6">${token.Ticker} Liquidity Pools</Typography>
            <Paper sx={{ overflow: "auto" }}>
              <InMemoryTable
                initialSortDir="desc"
                initialSortField="liquidity_usd"
                data={richTokenPools}
                headerCells={[
                  { label: "Pair" },
                  {
                    label: "24h Volume",
                    align: "right",
                    sortable: true,
                    field: "volume_usd",
                  },
                  {
                    label: "Liquidity",
                    align: "right",
                    sortable: true,
                    field: "liquidity_usd",
                  },
                ]}
                pageSize={30}
                renderRow={(entity: Pool) => (
                  <PoolRow
                    pool={entity}
                    overview={
                      overviewMap[entity.id] || (fullOverview ? fullOverview[entity.id] : null)
                    }
                  />
                )}
              />
            </Paper>
          </Stack>
        </Stack>
      </Grid2>
    </Grid2>
  )
}
